import { Input, Unifree } from '@yes.technology/react-toolkit'
import { useTranslation } from 'react-i18n-lite'
import ActivityInputDraw from '../../ActivityInput/ActivityInputDraw/ActivityInputDraw'
import { MULTISELECTION_SEPARATOR } from 'utils/constants'
import { StatusClass } from 'types/shared'

interface ActivityValueProps {
  uuid?: string
  label?: string
  value?: string | { id: string; des: string } | boolean | number
  contentType?: string
  required?: boolean
  statusClass?: StatusClass
  placeholder?: string
}

const multiselections = [
  'autocomplete_multiselection',
  'explicit_multiselection_objectclass',
  'explicit_multiselection_accordion'
]

function isMultiselection(contentType: string) {
  return multiselections.includes(contentType)
}

function getCommonValue(value: ActivityValueProps['value']) {
  switch (typeof value) {
    case 'string':
      return value
    case 'number':
      return value.toString()
    case 'boolean':
      return ''
    default:
      return value?.des ?? ''
  }
}

function getAgreementTermLabel(
  value: unknown,
  isChecked: boolean,
  t: (key: string) => string
): string {
  if (value === undefined) {
    return t('step.review.empty-state.common')
  }
  return isChecked
    ? t('activity-input-agreement-term.accept')
    : t('activity-input-agreement-term.declined')
}

const ActivityValue = ({
  uuid,
  label,
  value,
  contentType,
  required,
  statusClass,
  placeholder
}: ActivityValueProps) => {
  const { t } = useTranslation()

  const displayedLabel =
    contentType === 'agreement_term'
      ? t('activity-input-agreement-term.status')
      : label

  // todo: contentType === 'date', usando formato da lingua atual

  if (contentType === 'draw') {
    const inputValue = typeof value === 'string' ? value : ''

    if (!inputValue) {
      return (
        <Unifree
          id={uuid}
          label={label}
          value={t('step.review.empty-state.common')}
          heightClass='large'
          informational
          required={required}
          statusClass={statusClass}
        />
      )
    }

    return (
      <ActivityInputDraw
        inputValue={inputValue}
        onChange={() => {}}
        informational
      />
    )
  }

  if (contentType === 'agreement_term' || typeof value === 'boolean') {
    const isChecked =
      typeof value === 'string' ? JSON.parse(value)?.checked !== '0' : value

    return (
      <Unifree
        id={uuid}
        label={displayedLabel}
        value={getAgreementTermLabel(value, !!isChecked, t)}
        heightClass='large'
        informational
        required={required}
        statusClass={statusClass}
        enableLabelFading
        enableValueFading
      />
    )
  }

  const commonValue = getCommonValue(value)

  if (contentType && isMultiselection(contentType)) {
    const valueWithLinebreaks = commonValue
      .split(MULTISELECTION_SEPARATOR)
      .join('\n')
    return (
      <Input
        component='textarea'
        id={uuid}
        label={displayedLabel}
        value={valueWithLinebreaks}
        informational
        required={required}
        statusClass={statusClass}
        heightClass='regular'
        enableLabelFading
      />
    )
  }

  if (contentType === 'textarea') {
    return (
      <Input
        component='textarea'
        id={uuid}
        label={displayedLabel}
        value={commonValue}
        informational
        required={required}
        statusClass={statusClass}
        heightClass='regular'
        enableLabelFading
      />
    )
  }

  return (
    <Unifree
      id={uuid}
      label={displayedLabel}
      value={commonValue}
      heightClass='large'
      informational
      required={required}
      statusClass={statusClass}
      placeholder={placeholder}
      enableLabelFading
      enableValueFading
    />
  )
}

export default ActivityValue
